import React from 'react'

import { TextField, InputAdornment, useTheme } from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import ApartmentIcon from '@material-ui/icons/Apartment'

import { Container } from '../../components'
import { useUser } from '../../hooks'
import { typographyStyle } from '../../theme/typographyStyle'

interface TopNavigationProps {
    searchQuery: string
    setSearchQuery: (query: string) => void
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        header: {
            height: 72,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            padding: theme.spacing(0, 4),
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
        logoSection: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
        },
        logoIcon: {
            width: 44,
            height: 44,
            backgroundColor: theme.palette.primary.main,
            borderRadius: theme.spacing(1.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
        },
        titleContainer: {
            display: 'flex',
            flexDirection: 'column',
        },
        title: {
            ...(typographyStyle(theme.typography.h6) as Record<
                string,
                unknown
            >),
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.2,
        },
        subtitle: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
        },
        dot: {
            width: 4,
            height: 4,
            borderRadius: '50%',
            backgroundColor: theme.palette.text.disabled,
        },
        searchField: {
            width: 360,
            marginRight: theme.spacing(2),
            '& .MuiOutlinedInput-root': {
                borderRadius: theme.spacing(3),
                backgroundColor: theme.palette.grey[50],
                transition: 'all 0.2s ease',
                '&:hover': {
                    backgroundColor: theme.palette.grey[100],
                },
                '&.Mui-focused': {
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                },
                '& fieldset': {
                    borderColor: 'transparent',
                },
                '&:hover fieldset': {
                    borderColor: theme.palette.grey[300],
                },
                '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 1,
                },
            },
        },
    }),
)

export const TopNavigation = ({
    searchQuery,
    setSearchQuery,
}: TopNavigationProps) => {
    const theme = useTheme()
    const classes = useStyles()
    const { workspaceUser } = useUser()

    const propertyName = workspaceUser?.active_workspace?.name || 'Property'

    return (
        <Container
            className={classes.header}
            alignItems="center"
            justifyContent="flex-start"
        >
            <Container alignItems="center" style={{ flex: 1 }}>
                <div className={classes.logoSection}>
                    <div className={classes.logoIcon}>
                        <ApartmentIcon
                            style={{
                                color: theme.palette.common.white,
                                fontSize: 24,
                            }}
                        />
                    </div>
                    <div className={classes.titleContainer}>
                        <span className={classes.title}>{propertyName}</span>
                    </div>
                </div>
            </Container>

            <Container alignItems="center">
                <TextField
                    className={classes.searchField}
                    placeholder="Search units"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    style={{
                                        color: theme.palette.text.secondary,
                                        fontSize: 20,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    }}
                />
            </Container>
        </Container>
    )
}
